/**
 * RichTextEditor
 * TipTap-based rich text editor for Help article bodies. Operates on an HTML
 * string via value/onChange, matching this codebase's controlled-field
 * convention (see Input/Select).
 *
 * Article Images are inserted by the toolbar button, paste or drag & drop
 * (extensions/ArticleImageUpload.ts) and edited through ArticleImageMenu.
 * Uploading works before the article is first saved.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  Link as LinkIcon,
  ImagePlus
} from 'lucide-react';
import clsx from 'clsx';
import GenericModal from '@components/common/modals/GenericModal';
import Button from '@components/common/Button';
import { Input } from '@components/common';
import { isValidHttpUrl } from '@utils/stringUtils';
import { showToast } from '@utils/toast';
import { helpService } from '@services';
import ParagraphIndent from './extensions/ParagraphIndent';
import ArticleImage from './extensions/ArticleImage';
import ArticleImageUpload from './extensions/ArticleImageUpload';
import ArticleImageMenu from './ArticleImageMenu';
import { ALLOWED_IMAGE_TYPES } from './articleImageHelpers';
import styles from './RichTextEditor.module.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  error?: string | React.ReactNode;
  label?: string;
  required?: boolean;
  dataTestId?: string;
}

export const LINK_URL_ERROR = 'Enter a valid URL starting with http:// or https://';

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  dataTestId: string;
  children: React.ReactNode;
}

/**
 * A toolbar control that leaves the editor's selection intact.
 *
 * Pressing a plain <button> moves DOM focus out of the contenteditable before
 * the click fires, which collapses the document selection; the command then
 * runs against whatever ProseMirror re-derives - for block commands like
 * toggleBulletList/toggleOrderedList that is the whole document, so a list was
 * applied to every paragraph instead of the selected one. Cancelling mousedown
 * keeps focus (and therefore the selection range) where the author left it.
 */
const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive,
  disabled,
  title,
  dataTestId,
  children
}) => (
  <button
    type="button"
    className={clsx(styles.toolbarButton, isActive && styles.toolbarButtonActive)}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={isActive ?? false}
    data-test-id={dataTestId}
  >
    {children}
  </button>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  error,
  label,
  required,
  dataTestId = 'rich-text-editor'
}) => {
  // Mirrors the HTML this editor last emitted upward. External resets are
  // detected by comparing against this rather than against editor.getHTML():
  // getHTML() re-serializes the live doc, so a value the parent normalized
  // (or simply round-tripped) could differ from it and be misread as an
  // external reset - which would call setContent() mid-edit and destroy the
  // user's selection.
  const lastEmittedHtml = useRef<string>(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        // h2/h3 only: the article title already renders as the page's h1, and
        // the backend sanitizer strips anything outside this range.
        heading: { levels: [2, 3] }
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' }
      }),
      ParagraphIndent,
      ArticleImage,
      ArticleImageUpload.configure({
        upload: (file) => helpService.uploadImage(file),
        onError: (message) => showToast.error(message)
      })
    ],
    content: value,
    onUpdate: ({ editor: updatedEditor }) => {
      const html = updatedEditor.getHTML();
      lastEmittedHtml.current = html;
      onChange(html);
    }
  });

  // Toolbar highlighting has to follow the caret, not just document edits.
  // Reading editor.isActive() during render would go stale, because moving the
  // selection does not by itself re-render this component; useEditorState
  // subscribes to the editor and re-renders when these specific flags change.
  const activeMarks = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      bold: currentEditor?.isActive('bold') ?? false,
      italic: currentEditor?.isActive('italic') ?? false,
      underline: currentEditor?.isActive('underline') ?? false,
      heading2: currentEditor?.isActive('heading', { level: 2 }) ?? false,
      heading3: currentEditor?.isActive('heading', { level: 3 }) ?? false,
      blockquote: currentEditor?.isActive('blockquote') ?? false,
      bulletList: currentEditor?.isActive('bulletList') ?? false,
      orderedList: currentEditor?.isActive('orderedList') ?? false,
      link: currentEditor?.isActive('link') ?? false
    })
  });

  // Keep the editor in sync when the value is reset from outside (e.g. RHF
  // reset() when switching the selected article), without fighting the
  // editor's own onUpdate loop.
  useEffect(() => {
    if (!editor) return;
    if (value === lastEmittedHtml.current) return;
    lastEmittedHtml.current = value;
    editor.commands.setContent(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset so picking the same file again still fires a change event.
    event.target.value = '';
    if (!editor || files.length === 0) return;
    editor.chain().focus().insertArticleImageFiles(files).run();
  };

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkUrlError, setLinkUrlError] = useState<string | undefined>(undefined);

  /** Text currently covered by the selection, used to pre-fill the display text. */
  const getSelectedText = (): string => {
    if (!editor) return '';
    const { from, to } = editor.state.selection;
    return from === to ? '' : editor.state.doc.textBetween(from, to, ' ');
  };

  const openLinkModal = () => {
    if (!editor) return;
    const previousUrl = (editor.getAttributes('link').href as string | undefined) ?? '';
    setLinkUrl(previousUrl);
    setLinkText(getSelectedText());
    setLinkUrlError(undefined);
    setIsLinkModalOpen(true);
  };

  const closeLinkModal = () => {
    setIsLinkModalOpen(false);
    setLinkUrlError(undefined);
  };

  const handleRemoveLink = () => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLinkModal();
  };

  const handleSubmitLink = () => {
    if (!editor) return;

    const url = linkUrl.trim();
    if (url === '') {
      setLinkUrlError('URL is required');
      return;
    }
    if (!isValidHttpUrl(url)) {
      setLinkUrlError(LINK_URL_ERROR);
      return;
    }

    const text = linkText.trim();
    const hasSelection = !editor.state.selection.empty;

    if (hasSelection) {
      // Apply the link mark to what the author already selected.
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      // Nothing selected: insert the display text (defaulting to the URL) and
      // mark it. Built via TipTap commands rather than an HTML string so the
      // values are never interpolated into raw markup.
      editor
        .chain()
        .focus()
        .setLink({ href: url })
        .insertContent(text || url)
        .unsetMark('link')
        .run();
    }

    closeLinkModal();
  };

  const hasError = !!error;

  return (
    <div className={styles.wrapper} data-test-id={dataTestId}>
      {label && (
        <label className={styles.label} data-test-id={`${dataTestId}-label`}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={clsx(styles.editorContainer, hasError && styles.editorContainerError)}>
        <div className={styles.toolbar} role="toolbar" aria-label="Formatting" data-test-id={`${dataTestId}-toolbar`}>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={activeMarks?.heading2}
            disabled={!editor}
            title="Heading 2"
            dataTestId={`${dataTestId}-heading-2`}
          >
            <Heading2 size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={activeMarks?.heading3}
            disabled={!editor}
            title="Heading 3"
            dataTestId={`${dataTestId}-heading-3`}
          >
            <Heading3 size={16} />
          </ToolbarButton>

          <span className={styles.toolbarDivider} aria-hidden="true" />

          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleBold().run()}
            isActive={activeMarks?.bold}
            disabled={!editor}
            title="Bold"
            dataTestId={`${dataTestId}-bold`}
          >
            <Bold size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            isActive={activeMarks?.italic}
            disabled={!editor}
            title="Italic"
            dataTestId={`${dataTestId}-italic`}
          >
            <Italic size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            isActive={activeMarks?.underline}
            disabled={!editor}
            title="Underline"
            dataTestId={`${dataTestId}-underline`}
          >
            <UnderlineIcon size={16} />
          </ToolbarButton>

          <span className={styles.toolbarDivider} aria-hidden="true" />

          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            isActive={activeMarks?.bulletList}
            disabled={!editor}
            title="Bullet list"
            dataTestId={`${dataTestId}-bullet-list`}
          >
            <List size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            isActive={activeMarks?.orderedList}
            disabled={!editor}
            title="Numbered list"
            dataTestId={`${dataTestId}-ordered-list`}
          >
            <ListOrdered size={16} />
          </ToolbarButton>

          <span className={styles.toolbarDivider} aria-hidden="true" />

          <ToolbarButton
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            isActive={activeMarks?.blockquote}
            disabled={!editor}
            title="Quote"
            dataTestId={`${dataTestId}-blockquote`}
          >
            <Quote size={16} />
          </ToolbarButton>

          <span className={styles.toolbarDivider} aria-hidden="true" />

          <ToolbarButton
            onClick={openLinkModal}
            isActive={activeMarks?.link}
            disabled={!editor}
            title="Link"
            dataTestId={`${dataTestId}-link`}
          >
            <LinkIcon size={16} />
          </ToolbarButton>

          <span className={styles.toolbarDivider} aria-hidden="true" />

          <ToolbarButton
            onClick={() => imageInputRef.current?.click()}
            disabled={!editor}
            title="Insert image"
            dataTestId={`${dataTestId}-image`}
          >
            <ImagePlus size={16} />
          </ToolbarButton>
          <input
            ref={imageInputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(',')}
            multiple
            hidden
            onChange={handleImageFilesPicked}
            data-test-id={`${dataTestId}-image-input`}
          />
        </div>

        <EditorContent
          editor={editor}
          className={styles.editorContent}
          data-test-id={`${dataTestId}-content`}
        />

        <ArticleImageMenu editor={editor} dataTestId={dataTestId} />
      </div>

      {error && (
        <div className={styles.errorMessage} role="alert" data-test-id={`${dataTestId}-error`}>
          {error}
        </div>
      )}

      <GenericModal
        isOpen={isLinkModalOpen}
        onClose={closeLinkModal}
        title={editor?.isActive('link') ? 'Edit Link' : 'Insert Link'}
        size="sm"
        disableBackdropClick
        dataTestId={`${dataTestId}-link-modal`}
        footer={
          <>
            {editor?.isActive('link') && (
              <Button
                variant="secondary"
                onClick={handleRemoveLink}
                data-test-id={`${dataTestId}-link-remove`}
              >
                Remove Link
              </Button>
            )}
            <Button variant="secondary" onClick={closeLinkModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmitLink} data-test-id={`${dataTestId}-link-submit`}>
              Apply
            </Button>
          </>
        }
      >
        <Input
          label="URL"
          required
          value={linkUrl}
          onChange={(e) => {
            setLinkUrl(e.target.value);
            if (linkUrlError) setLinkUrlError(undefined);
          }}
          placeholder="https://example.com"
          error={linkUrlError}
          dataTestId={`${dataTestId}-link-url`}
        />
        <Input
          label="Display Text"
          value={linkText}
          onChange={(e) => setLinkText(e.target.value)}
          placeholder="Defaults to the URL"
          dataTestId={`${dataTestId}-link-text`}
        />
      </GenericModal>
    </div>
  );
};

export default RichTextEditor;
